import * as React from "react";
import { useSelector } from "react-redux";
import { useSpring, animated } from "react-spring";
import { useTranslation } from "react-i18next";
import { flatMap } from "../../../utils/lodash";
import useStyles from "./VariablesPanel.styles";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import { StoreState } from "../../../models/states/StoreState";
import { VariableType } from "../../../models/enums/VariableType";
import WidgetPanel from "./WidgetPanel";

interface VariableProps {
    variable: string;
    value: string;
    text: string;
    openEnd?: string;
    type?: VariableType;
}

const getFilteredVariableKeys = (variables, filter) =>
    Object.keys(variables).filter((key) => key.toLowerCase().includes(filter));

function Variable({ variable, value, text, openEnd, type }: VariableProps) {
    const isSystemVariable = type === VariableType.System;
    const [showDetails, setShowDetails] = React.useState(false);
    const props = useSpring({
        height: showDetails ? 152 : 0,
        opacity: showDetails ? 1 : 0,
    });
    const classes = useStyles({ isSystemVariable });
    const { t } = useTranslation();
    return (
        <div className={classes.variableContainer}>
            <div className={classes.variableValue}>
                <span className={classes.variableName}>{variable}</span>
                <span>=</span>
                <input className={classes.variableInputSmall} value={value} type="text" readOnly />
                <div
                    className={classes.variableDetailsIcon}
                    onClick={() => setShowDetails((s) => !s)}
                >
                    <FontAwesomeIcon
                        fixedWidth
                        icon={["far", showDetails ? "chevron-up" : "chevron-down"]}
                    />
                </div>
            </div>
            <animated.div className={classes.variableDetails} style={props}>
                <div>
                    <div className={classes.variableText}>{t("debug.panels.variables.text")}</div>
                    <input className={classes.variableInput} value={text} type="text" readOnly />
                </div>
                <div>
                    <div className={classes.variableOpenEnd}>
                        {t("debug.panels.variables.openEnd")}
                    </div>
                    <textarea
                        className={classes.variableInput}
                        name="variableOpenEnd"
                        rows={4}
                        readOnly
                    >
                        {openEnd}
                    </textarea>
                </div>
            </animated.div>
        </div>
    );
}

export function VariablesPanel() {
    const containerRef = React.useRef();
    const [search, setSearch] = React.useState("");
    const [showSystemVariables] = React.useState(false);
    const lowerCaseSearch = search.toLowerCase();
    const { t } = useTranslation();

    const system = useSelector((state: StoreState) => state.interview.variables.system);
    const blocks = useSelector((state: StoreState) => state.interview.questionnaire.blocks);
    const mentions = useSelector(
        (state: StoreState) => state.interview.variables.mentionsByVariableName,
    );

    const systemVariables = getFilteredVariableKeys(system, lowerCaseSearch).map((key) => (
        <Variable
            key={key}
            variable={key}
            value={system[key].code}
            text={system[key].text}
            type={VariableType.System}
        />
    ));

    const filteredVariableKeys = getFilteredVariableKeys(mentions, lowerCaseSearch);
    const orderedVariableKeys = flatMap(
        flatMap(blocks, "questions"),
        "variableNames",
    ).filter((key) => filteredVariableKeys.includes(key));

    const questionVariables = flatMap(orderedVariableKeys, (key) =>
        mentions[key].map((mention) => (
            <Variable
                key={`${key} (${mention.index})`}
                variable={`${key} (${mention.index})`}
                value={mention.value}
                text={mention.value}
                openEnd={mention.openEnd}
            />
        )),
    );

    return (
        <WidgetPanel
            searchable
            search={search}
            setSearch={setSearch}
            searchPlaceholder={t("debug.panels.variables.search")}
            containerRef={containerRef}
            title={t("debug.panels.variables.title")}
        >
            {showSystemVariables && systemVariables}
            {questionVariables}
        </WidgetPanel>
    );
}
